import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useConfirmAppointment } from "@/lib/mutations";
import { useSlotBlocks, usePublicAppSettings, useSlotCapacity, useEffectiveSlotConfig } from "@/lib/queries";
import { isSlotBlocked, dayUnavailableReason } from "@emr/shared/scheduling";
import { DEFAULT_WEEKLY_SCHEDULE } from "@emr/shared/scheduling";
import { dateIST, todayIST, fmtDate } from "@emr/shared/india";
import { capFor } from "./slotCapacity";
import type { AppointmentRow } from "@/lib/queries";

/**
 * The IST HH:mm of a stored arrivalTime, or "" when the timestamp is
 * date-only (midnight UTC — how review-flow pendings store the review
 * date). Slot-bound pendings carry a real requested time and get prefilled.
 */
function istTime(iso: string): string {
  const d = new Date(iso);
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0) return ""; // date-only (review date)
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

export function ConfirmAppointmentDialog({ open, onOpenChange, appointment }: { open: boolean; onOpenChange: (v: boolean) => void; appointment: AppointmentRow | null }) {
  const [slot, setSlot] = useState<string>("");
  const [arrival, setArrival] = useState("");
  const confirm = useConfirmAppointment(appointment?.id ?? "");
  const { data: blocksData } = useSlotBlocks();
  const { data: settings } = usePublicAppSettings();
  const blocks = blocksData?.rows ?? [];

  // The date this pending is FOR: its own arrivalTime. For review-flow
  // pendings that IS the review date the EMR visit page saved; for
  // slot-bound pendings it's the requested day. Slot-less pendings (no
  // arrivalTime) default to today. NEVER use createdAt — that is the day the
  // pending row was created (the visit day), which booked confirmed
  // appointments onto a wrong, usually past date (audit 2026-10-05, appt
  // b5836928: created 2026-09-30, confirmed onto 2026-09-30 instead of the
  // review date; reception had to re-book manually a minute later).
  const date = appointment?.arrivalTime ? dateIST(appointment.arrivalTime) : todayIST();

  // The pending's OWN date config drives the slot buttons (Phase 3).
  const { slotList } = useEffectiveSlotConfig(date, settings);
  const { data: capacity } = useSlotCapacity(date);

  // Reset the draft whenever a different appointment is opened: slot-bound
  // pendings already carry the requested slot + time, so prefill them;
  // review-flow pendings are date-only, so the time starts blank.
  useEffect(() => {
    if (!appointment) return;
    setArrival(appointment.arrivalTime ? istTime(appointment.arrivalTime) : "");
    // Prefill the slot only when its key still exists in the date's config;
    // otherwise leave blank — the auto-switch effect below picks a default.
    const prefilled = appointment.slot && slotList.some((s) => s.key === appointment.slot)
      ? appointment.slot
      : "";
    setSlot(prefilled);
  }, [appointment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-switch (Phase 3): when the prefilled slot no longer exists in the
  // config, or is blocked / full, switch to the first non-blocked slot with
  // remaining capacity. Runs once the config + blocks + capacity resolve.
  useEffect(() => {
    if (!appointment || slotList.length === 0) return;
    const dObj = new Date(date + "T00:00:00");
    // Shared per-key capacity lookup (slotCapacity.ts) — same map-first,
    // legacy-fallback rule as the other appointment dialogs.
    const ok = (key: string) => {
      if (isSlotBlocked(dObj, key, blocks as any)) return false;
      const cap = capFor(capacity, key);
      if (cap && cap.max > 0 && cap.booked >= cap.max) return false;
      return true;
    };
    setSlot((cur) => {
      if (cur && ok(cur)) return cur;
      const first = slotList.find((s) => ok(s.key));
      return first ? first.key : cur;
    });
  }, [appointment?.id, slotList, blocksData, capacity, date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Day-level unavailability for the pending's own date (2026-10-07): a
  // legacy pending dated on a no-OPD day (off weekday / whole-day block made
  // after it was requested) shows an inline warning and Confirm is disabled —
  // the server (/confirm) rejects it with 422 date_not_working/date_blocked.
  const weeklySchedule = (settings?.weeklySchedule as never) ?? DEFAULT_WEEKLY_SCHEDULE;
  const dayUnavailable = dayUnavailableReason(new Date(date + "T00:00:00"), weeklySchedule, blocks as any);

  const submit = async () => {
    if (!appointment || !arrival || !slot || dayUnavailable) return;
    // Same construction as BookDialog: local (IST) wall time → UTC ISO.
    const arrivalIso = new Date(`${date}T${arrival}:00`).toISOString();
    try {
      await confirm.mutateAsync({ slot, arrivalTime: arrivalIso });
      onOpenChange(false);
    } catch { /* handled */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Confirm appointment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted">
            {appointment?.patientName} · {appointment?.clinicUhid}
            {appointment ? <> · for <span className="font-medium">{fmtDate(date)}</span></> : null}
          </div>
          <div>
            <Label>Slot</Label>
            <div className="grid grid-cols-2 gap-2">
              {slotList.map((def) => {
                // The pending's own date (arrivalTime) — never createdAt.
                const blocked = isSlotBlocked(new Date(date + "T00:00:00"), def.key, blocks as any);
                return (
                  <button
                    key={def.key}
                    type="button"
                    disabled={blocked}
                    onClick={() => setSlot(def.key)}
                    className={`rounded-lg border p-2 text-sm ${slot === def.key ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200"} ${blocked ? "opacity-40 cursor-not-allowed" : ""}`}
                    title={blocked ? "This slot is blocked for this date" : ""}
                  >
                    {def.label}{blocked ? " · Blocked" : ""}
                  </button>
                );
              })}
            </div>
          </div>
          {dayUnavailable && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-2 text-xs font-medium text-red-700">
              {dayUnavailable.kind === "blocked"
                ? `Whole day blocked${dayUnavailable.reason ? ` — ${dayUnavailable.reason}` : ""} (Settings → Block Days). Reschedule this pending to a working day first.`
                : "This date is not a working day per the weekly schedule (Settings → Slot Config). Reschedule this pending first."}
            </div>
          )}
          <div>
            <Label>Arrival time</Label>
            <Input type="time" value={arrival} onChange={(e) => setArrival(e.target.value)} autoFocus />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!arrival || !slot || !!dayUnavailable || confirm.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
